import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { ProductImagePlaceholder } from "./Logo";

export function Stars({ rating = 0, size = 13, testid = "star-rating" }) {
  if (!rating) return null;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} strokeWidth={1.2}
          className={i <= Math.round(rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3a3a3a]"} />
      ))}
    </span>
  );
}

export default function ProductCard({ product, index = 0, imageOverride = "" }) {
  const { addToCart } = useShop();
  const displayImage = imageOverride || product.image_url;
  const minPrice = product.sizes.length ? Math.min(...product.sizes.map((s) => s.price)) : 0;
  const defaultSize = product.sizes.find((s) => s.stock > 0) || product.sizes[0];
  const inStock = product.sizes.some((s) => s.stock > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative bg-[#0D0D0D] gold-hairline hover:border-[#D4AF37]/60 transition-all duration-500 hover:gold-glow flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block relative aspect-[3/4] overflow-hidden" data-testid={`product-card-link-${product.id}`}>
        {displayImage ? (
          <img
            src={imageUrl(displayImage)}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full transition-transform duration-700 ease-out group-hover:scale-105">
            <ProductImagePlaceholder name={product.name} className="w-full h-full" />
          </div>
        )}
        {product.is_bestseller && (
          <span className="absolute top-3 left-3 bg-[#D4AF37] text-[#080808] font-sans-body text-[9px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">
            Bestseller
          </span>
        )}
        {!inStock && (
          <span className="absolute inset-0 bg-black/60 flex items-center justify-center font-sans-body text-xs uppercase tracking-[0.3em] text-white/80">
            Sold Out
          </span>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <p className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B]">{product.category}</p>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif-display text-xl text-white mt-1.5 leading-tight group-hover:text-[#D4AF37] transition-colors">
            {product.name}
          </h3>
        </Link>
        {(product.short_description || product.description) && (
          <p className="font-sans-body text-xs text-[#B8B8B8] font-light mt-2 line-clamp-2">{product.short_description || product.description}</p>
        )}
        <div className="mt-3">
          <Stars rating={product.rating_average} count={product.rating_count} testid={`product-stars-${product.id}`} />
        </div>
        <div className="mt-auto pt-4 flex items-center justify-between">
          <span className="font-mono-price text-[#D4AF37]" data-testid={`product-price-${product.id}`}>
            ${minPrice.toFixed(2)}{product.sizes.length > 1 && <span className="text-[10px] text-[#B8B8B8]"> +</span>}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => defaultSize && addToCart(product, defaultSize.label, 1)}
            disabled={!inStock}
            className="bg-[#D4AF37] text-[#080808] font-sans-body text-[10px] font-bold uppercase tracking-[0.15em] py-2.5 hover:bg-[#F1DC9A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`product-add-to-cart-${product.id}`}
          >
            Add to Bag
          </button>
          <Link
            to={`/product/${product.id}`}
            className="border border-[#D4AF37]/40 text-[#D4AF37] font-sans-body text-[10px] font-bold uppercase tracking-[0.15em] py-2.5 text-center hover:bg-[#D4AF37]/10 transition-colors"
            data-testid={`product-view-details-${product.id}`}
          >
            Details
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
